/**
 * Наборы (BOM) — нормативы расхода материалов на типы беседок.
 *
 * Концепция: каждый набор = «1 беседка X м²» с фиксированным списком
 * материалов и количеств. Привязывается к production_objects.kit_id →
 * страница Планирования автоматически считает «на N объектов нужно Y
 * материала».
 */
import { Fragment, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { ItemAutocomplete } from "@/components/warehouse/ItemAutocomplete";
import {
  addKitItem, archiveKit, createKit, fetchKitItems, fetchKits,
  removeKitItem, updateKit,
  type KitRow, type KitItemPayload, type KitPayload, type ItemRow,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";
import { usePermissions } from "@/lib/usePermissions";


export function WarehouseKitsPage() {
  const can = usePermissions();
  const qc = useQueryClient();
  const [selectedKitId, setSelectedKitId] = useState<number | null>(null);
  const [creatingOpen, setCreatingOpen] = useState(false);

  const kitsQ = useQuery({
    queryKey: ["warehouse-kits"],
    queryFn: () => fetchKits(),
  });
  const kits = kitsQ.data || [];

  // Авто-выбираем первый набор, если ничего не выбрано
  const effectiveKitId = useMemo(
    () => selectedKitId ?? (kits.length > 0 ? kits[0].id : null),
    [selectedKitId, kits],
  );

  const itemsQ = useQuery({
    queryKey: ["warehouse-kit-items", effectiveKitId],
    queryFn: () => fetchKitItems(effectiveKitId!),
    enabled: effectiveKitId != null,
  });
  const items = itemsQ.data || [];
  const selectedKit = kits.find(k => k.id === effectiveKitId) || null;

  const addItemMut = useMutation({
    mutationFn: (payload: KitItemPayload) => addKitItem(effectiveKitId!, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-kit-items", effectiveKitId] });
      void qc.invalidateQueries({ queryKey: ["warehouse-kits"] });
    },
  });

  const removeItemMut = useMutation({
    mutationFn: (itemId: number) => removeKitItem(effectiveKitId!, itemId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-kit-items", effectiveKitId] });
      void qc.invalidateQueries({ queryKey: ["warehouse-kits"] });
    },
  });

  const updateQtyMut = useMutation({
    mutationFn: (vars: { itemId: number; quantity: number }) =>
      addKitItem(effectiveKitId!, { item_id: vars.itemId, quantity: vars.quantity }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-kit-items", effectiveKitId] });
    },
  });

  if (kitsQ.isLoading) return <Spinner size="lg" label="Загрузка наборов…" />;
  if (kitsQ.error) {
    const err = kitsQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }

  // Группировка позиций по секциям (как в Остатках)
  const grouped: Record<string, typeof items> = {};
  for (const it of items) {
    const sec = it.item_section || "—";
    if (!grouped[sec]) grouped[sec] = [];
    grouped[sec].push(it);
  }

  return (
    <div className="page">

      {kits.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 18, fontFamily: "var(--serif)", marginBottom: 8 }}>Наборов пока нет</div>
          <div className="muted" style={{ marginBottom: 16 }}>
            Набор (BOM) — это нормативы расхода материалов на 1 беседку определённого типа.<br/>
            После создания набора привяжите его к проектам в Производстве — и страница
            «Планирование» покажет «на N объектов нужно X материалов».
          </div>
          {can("kits.edit") && (
            <button type="button" className="btn btn-primary"
              onClick={() => setCreatingOpen(true)}
              style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "8px 18px", fontWeight: 600, cursor: "pointer" }}>
              + Создать первый набор
            </button>
          )}
          {creatingOpen && <KitFormModal onClose={() => setCreatingOpen(false)} onCreated={(id) => { setSelectedKitId(id); setCreatingOpen(false); }} />}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 280px) 1fr", gap: "var(--gap)" }}>
          {/* Левая колонка — список наборов */}
          <div className="card" style={{ padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Наборы</div>
              {can("kits.edit") && (
                <button type="button" className="btn btn-sm" onClick={() => setCreatingOpen(true)} title="Новый набор">+</button>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {kits.map(k => {
                const active = k.id === effectiveKitId;
                return (
                  <button
                    key={k.id} type="button"
                    onClick={() => setSelectedKitId(k.id)}
                    style={{
                      textAlign: "left", padding: "8px 10px", borderRadius: 4,
                      background: active ? "var(--paper)" : "transparent",
                      border: active ? "1px solid var(--line)" : "1px solid transparent",
                      cursor: "pointer", fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: active ? 700 : 500 }}>{k.name}</div>
                    <div style={{ fontSize: 11, color: "var(--mute)" }}>
                      {k.sqm != null && <>{k.sqm} м² · </>}
                      {k.items_count} поз.
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Правая — детали выбранного набора */}
          {selectedKit && (
            <KitDetails
              kit={selectedKit}
              items={items}
              grouped={grouped}
              loading={itemsQ.isLoading}
              onAdd={(it: ItemRow) => addItemMut.mutate({ item_id: it.id, quantity: 1 })}
              onUpdateQty={(itemId, qty) => updateQtyMut.mutate({ itemId, quantity: qty })}
              onRemove={(itemId) => {
                if (!confirm("Убрать позицию из набора?")) return;
                removeItemMut.mutate(itemId);
              }}
              onArchive={() => {
                if (!confirm(`Архивировать «${selectedKit.name}»? Привязки к проектам сохранятся, но набор скроется из списка.`)) return;
                archiveKit(selectedKit.id).then(() => {
                  void qc.invalidateQueries({ queryKey: ["warehouse-kits"] });
                  setSelectedKitId(null);
                });
              }}
            />
          )}
          {creatingOpen && <KitFormModal onClose={() => setCreatingOpen(false)} onCreated={(id) => { setSelectedKitId(id); setCreatingOpen(false); }} />}
        </div>
      )}
    </div>
  );
}


function KitDetails({
  kit, items, grouped, loading, onAdd, onUpdateQty, onRemove, onArchive,
}: {
  kit: KitRow;
  items: any[];
  grouped: Record<string, any[]>;
  loading: boolean;
  onAdd: (it: ItemRow) => void;
  onUpdateQty: (itemId: number, qty: number) => void;
  onRemove: (itemId: number) => void;
  onArchive: () => void;
}) {
  const qc = useQueryClient();
  const [editingMeta, setEditingMeta] = useState(false);
  const [draftQty, setDraftQty] = useState<Record<number, string>>({});

  const updateKitMut = useMutation({
    mutationFn: (payload: KitPayload) => updateKit(kit.id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-kits"] });
      setEditingMeta(false);
    },
  });

  return (
    <div className="card" style={{ padding: 16 }}>
      {/* Шапка */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700 }}>{kit.name}</div>
          <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
            {kit.sku && <>SKU: <span className="tabular">{kit.sku}</span> · </>}
            {kit.sqm != null && <>{kit.sqm} м² · </>}
            Позиций: {items.length}
          </div>
          {kit.description && (
            <div style={{ fontSize: 12, marginTop: 6, color: "var(--ink-3)" }}>{kit.description}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="btn btn-sm" onClick={() => setEditingMeta(v => !v)}>
            {editingMeta ? "Отмена" : "Изменить"}
          </button>
          <button type="button" className="btn btn-sm" onClick={onArchive} style={{ color: "var(--brick)" }}>
            Архивировать
          </button>
        </div>
      </div>

      {editingMeta && (
        <KitEditMeta kit={kit} onSubmit={(p) => updateKitMut.mutate(p)} pending={updateKitMut.isPending} />
      )}

      {/* Поиск позиций для добавления */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: 8, background: "var(--paper)", borderRadius: 4 }}>
        <Icon name="search" size={14}/>
        <span style={{ fontSize: 12, color: "var(--mute)" }}>Добавить позицию в набор:</span>
        <div style={{ flex: 1, maxWidth: 500 }}>
          <ItemAutocomplete onSelect={onAdd} placeholder="Поиск по артикулу или наименованию" />
        </div>
      </div>

      {/* Таблица позиций набора */}
      {loading ? (
        <Spinner label="Загрузка позиций набора…" />
      ) : items.length === 0 ? (
        <div className="muted" style={{ textAlign: "center", padding: "30px 20px" }}>
          В наборе пока нет позиций. Добавьте через поиск выше.
        </div>
      ) : (
        <div className="tbl-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Артикул</th>
                <th>Наименование</th>
                <th style={{ width: 70 }}>Ед.</th>
                <th className="num" style={{ width: 110 }}>Кол-во / 1 беседку</th>
                <th style={{ width: 50 }}/>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([sec, group]) => (
                <Fragment key={sec}>
                  <tr style={{ background: "var(--paper)" }}>
                    <td colSpan={5} style={{ fontWeight: 700, fontSize: 13, color: "var(--ink-3)" }}>{sec}</td>
                  </tr>
                  {group.map(it => (
                    <tr key={it.id}>
                      <td className="tabular">{it.item_sku || "—"}</td>
                      <td>{it.item_name}</td>
                      <td>{it.item_unit || "—"}</td>
                      <td className="num">
                        <input
                          type="text" inputMode="decimal" className="input-sm"
                          style={{ width: "100%", textAlign: "right", fontSize: 12 }}
                          value={draftQty[it.item_id] ?? String(it.quantity)}
                          onChange={(e) => setDraftQty(d => ({ ...d, [it.item_id]: e.target.value.replace(",", ".") }))}
                          onBlur={() => {
                            const raw = draftQty[it.item_id];
                            if (raw === undefined) return;
                            const v = parseFloat(raw);
                            if (!isNaN(v) && v > 0 && v !== it.quantity) {
                              onUpdateQty(it.item_id, v);
                            }
                            setDraftQty(d => { const x = { ...d }; delete x[it.item_id]; return x; });
                          }}
                        />
                      </td>
                      <td>
                        <button type="button" className="btn btn-sm" onClick={() => onRemove(it.item_id)} title="Убрать" style={{ color: "var(--brick)" }}>×</button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function KitEditMeta({ kit, onSubmit, pending }: { kit: KitRow; onSubmit: (p: KitPayload) => void; pending: boolean }) {
  const [form, setForm] = useState<KitPayload>({
    name: kit.name, sku: kit.sku, sqm: kit.sqm, description: kit.description,
  });
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      style={{ marginBottom: 16, padding: 12, background: "var(--paper)", borderRadius: 4 }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 130px", gap: 8 }}>
        <input className="input-sm" placeholder="Название" required value={form.name}
               onChange={e => setForm({ ...form, name: e.target.value })} />
        <input className="input-sm" placeholder="SKU" value={form.sku || ""}
               onChange={e => setForm({ ...form, sku: e.target.value || null })} />
        <input className="input-sm" type="number" inputMode="decimal" step="0.01" placeholder="м²" value={form.sqm ?? ""}
               onChange={e => setForm({ ...form, sqm: e.target.value === "" ? null : parseFloat(e.target.value) })} />
      </div>
      <textarea className="input-sm" placeholder="Описание (опционально)"
                style={{ width: "100%", marginTop: 8, minHeight: 50 }}
                value={form.description || ""}
                onChange={e => setForm({ ...form, description: e.target.value || null })} />
      <div style={{ marginTop: 8, textAlign: "right" }}>
        <button type="submit" className="btn btn-primary" disabled={pending}
                style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 14px", fontWeight: 600, cursor: "pointer" }}>
          {pending ? "..." : "Сохранить"}
        </button>
      </div>
    </form>
  );
}


function KitFormModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const [form, setForm] = useState<KitPayload>({ name: "", sku: "", sqm: null, description: "" });
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => createKit({
      name: form.name.trim(), sku: form.sku?.trim() || null,
      sqm: form.sqm, description: form.description?.trim() || null,
    }),
    onSuccess: (data) => onCreated(data.id),
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Введите название набора"); return; }
    mut.mutate();
  }
  return (
    <div role="dialog" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 16 }}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}
            className="card" style={{ width: "100%", maxWidth: 460, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>Новый набор (BOM)</div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>Название *</div>
          <input className="input-sm" required autoFocus style={{ width: "100%" }}
                 value={form.name}
                 onChange={e => setForm({ ...form, name: e.target.value })}
                 placeholder='Например: «Беседка 4×4 классическая»' />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>SKU</div>
            <input className="input-sm" style={{ width: "100%" }} value={form.sku || ""}
                   onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="опц." />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>Площадь, м²</div>
            <input className="input-sm" type="number" inputMode="decimal" step="0.01" style={{ width: "100%" }}
                   value={form.sqm ?? ""}
                   onChange={e => setForm({ ...form, sqm: e.target.value === "" ? null : parseFloat(e.target.value) })} />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>Описание</div>
          <textarea className="input-sm" style={{ width: "100%", minHeight: 60 }}
                    value={form.description || ""}
                    onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>
        {error && <div className="alert alert-red" style={{ marginBottom: 8 }}><div className="alert-body">{error}</div></div>}
        <div style={{ textAlign: "right" }}>
          <button type="submit" className="btn btn-primary" disabled={mut.isPending || !form.name.trim()}
                  style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, cursor: "pointer" }}>
            {mut.isPending ? "Создание…" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
}
